import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Divider,
  Badge,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  LinearProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  Analytics as AnalyticsIcon,
  Group as GroupIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Verified as VerifiedIcon,
  Gavel as GavelIcon,
  FactCheck as FactCheckIcon,
  AutoFixHigh as AutoFixHighIcon,
  Speed as SpeedIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  History as HistoryIcon,
  TrendingDown as TrendingDownIcon,
  LocalAtm as AtmIcon,
  AccountBalanceWallet as WalletIcon,
  MonetizationOn as MonetizationIcon
} from '@mui/icons-material';
import Logo from '../common/Logo';
import { getPaymentHistory, refundPayment, PaymentHistoryPayment } from '../../services/paymentApi';

interface Payment {
  id: string;
  transactionId: string;
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'cancelled';
  paymentMethod: 'credit_card' | 'bank_transfer' | 'paypal' | 'stripe';
  description: string;
  prTitle: string;
  prId: string;
  channels: string[];
   totalChannels?: number;
   stripePaymentIntentId?: string;
   stripeSessionId?: string;
  createdAt: string;
  completedAt?: string;
  refundedAt?: string;
  refundAmount?: number;
  refundReason?: string;
  refundRequestStatus?: 'pending' | 'approved' | 'rejected' | 'processed';
  refundRequestedAt?: string;
  refundNotes?: string;
  customerEmail?: string;
  customerName?: string;
  billingAddress?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  metadata?: {
    campaignId?: string;
    invoiceNumber?: string;
    taxAmount?: number;
    discountAmount?: number;
  };
  // Package / credits info
  packageType?: 'package' | 'standalone' | 'custom';
  packageTier?: string;
  packageCredits?: number;
  stripeLiveMode?: boolean; // false = test, true = live, undefined = legacy
}

const Payments: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<Payment[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refundAmount, setRefundAmount] = useState<number | ''>('');
  const [refundReason, setRefundReason] = useState<string>('');
  const [refundNotes, setRefundNotes] = useState<string>('');
  const [isProcessingRefund, setIsProcessingRefund] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const mapApiPaymentToPayment = (apiPayment: PaymentHistoryPayment): Payment => {
    const transactionId =
      apiPayment.transactionId ||
      apiPayment.stripePaymentIntentId ||
      apiPayment.stripeSessionId ||
      `PAY-${apiPayment._id.substring(0, 8)}`;

    const prTitle = apiPayment.prId?.title || 'Press Release Distribution';
    const prId = apiPayment.prId?._id || '';

    // Calculate amount from channels if amount is 0 or missing
    let calculatedAmount = apiPayment.amount || 0;
    if ((calculatedAmount === 0 || !calculatedAmount) && apiPayment.channels && apiPayment.channels.length > 0) {
      calculatedAmount = apiPayment.channels.reduce((sum, channel) => sum + (channel.price || 0), 0);
    }

    return {
      id: apiPayment._id,
      transactionId,
      amount: calculatedAmount,
      currency: apiPayment.currency || 'USD',
      status: apiPayment.status,
      paymentMethod: apiPayment.paymentMethod === 'stripe' ? 'stripe' : apiPayment.paymentMethod,
      description: prTitle,
      prTitle,
      prId,
      channels: (apiPayment.channels || []).map((c) => c.channelName),
      totalChannels: apiPayment.totalChannels,
      stripePaymentIntentId: apiPayment.stripePaymentIntentId,
      stripeSessionId: apiPayment.stripeSessionId,
      createdAt: apiPayment.createdAt,
      completedAt: apiPayment.paidAt,
      refundedAt: apiPayment.refundedAt,
      refundAmount: apiPayment.refundAmount,
      refundReason: apiPayment.refundReason,
      refundRequestStatus: (apiPayment as any).refundRequestStatus,
      refundRequestedAt: (apiPayment as any).refundRequestedAt,
      refundNotes: (apiPayment as any).refundNotes,
      customerEmail: undefined,
      customerName: undefined,
      billingAddress: undefined,
      metadata: {
        taxAmount: undefined,
        discountAmount: undefined
      },
      packageType: apiPayment.packageType,
      packageTier: apiPayment.packageTier,
      packageCredits: apiPayment.packageCredits,
      stripeLiveMode: apiPayment.stripeLiveMode
    };
  };

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPaymentHistory(1, 100);
      const mapped = response.payments.map(mapApiPaymentToPayment);
      setPayments(mapped);
      setFilteredPayments(mapped);
    } catch (err: any) {
      console.error('Failed to load payments:', err);
      setError(err.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // Filter payments
  useEffect(() => {
    let filtered = payments;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(payment => {
        const transactionId = payment.transactionId?.toLowerCase() || '';
        const description = payment.description?.toLowerCase() || '';
        const customerName = payment.customerName?.toLowerCase() || '';
        const customerEmail = payment.customerEmail?.toLowerCase() || '';

        return (
          transactionId.includes(term) ||
          description.includes(term) ||
          customerName.includes(term) ||
          customerEmail.includes(term)
        );
      });
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(payment => payment.status === statusFilter);
    }

    if (methodFilter !== 'all') {
      filtered = filtered.filter(payment => payment.paymentMethod === methodFilter);
    }

    setFilteredPayments(filtered);
  }, [searchTerm, statusFilter, methodFilter, payments]);

  // Get display status based on payment status and refund request status
  const getDisplayStatus = (payment: Payment): string => {
    // If refund request is pending or approved, show pending status
    if (payment.refundRequestStatus === 'pending' || payment.refundRequestStatus === 'approved') {
      return 'pending';
    }
    // Only show refunded if refund request is processed or payment status is refunded without pending request
    if (payment.refundRequestStatus === 'processed' || (payment.status === 'refunded' && !payment.refundRequestStatus)) {
      return 'refunded';
    }
    // Otherwise return the original status
    return payment.status;
  };

  const getStatusIcon = (status: string, refundRequestStatus?: string) => {
    // If there's a pending or approved refund request, show pending icon
    if (refundRequestStatus === 'pending' || refundRequestStatus === 'approved') {
      return <PendingIcon style={{ color: '#FF9800' }} />;
    }
    switch (status) {
      case 'completed': return <CheckCircleIcon style={{ color: '#4CAF50' }} />;
      case 'pending': return <PendingIcon style={{ color: '#FF9800' }} />;
      case 'failed': return <ErrorIcon style={{ color: '#F44336' }} />;
      case 'refunded': return <RefreshIcon style={{ color: '#2196F3' }} />;
      case 'cancelled': return <StopIcon style={{ color: '#9E9E9E' }} />;
      default: return <PendingIcon style={{ color: '#9E9E9E' }} />;
    }
  };

  const getStatusColor = (status: string, refundRequestStatus?: string) => {
    // If there's a pending or approved refund request, show pending color
    if (refundRequestStatus === 'pending' || refundRequestStatus === 'approved') {
      return '#FF9800';
    }
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'pending': return '#FF9800';
      case 'failed': return '#F44336';
      case 'refunded': return '#2196F3';
      case 'cancelled': return '#9E9E9E';
      default: return '#9E9E9E';
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'credit_card': return <CreditCardIcon style={{ fontSize: '16px' }} />;
      case 'bank_transfer': return <BankIcon style={{ fontSize: '16px' }} />;
      case 'paypal': return <MonetizationIcon style={{ fontSize: '16px' }} />;
      case 'stripe': return <SecurityIcon style={{ fontSize: '16px' }} />;
      default: return <PaymentIcon style={{ fontSize: '16px' }} />;
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, payment: Payment) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayment(payment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPayment(null);
  };

  const handleViewPayment = () => {
    setViewDialogOpen(true);
    handleMenuClose();
  };

  const handleRefundPayment = () => {
    console.log('Opening refund dialog', { selectedPayment, amount: selectedPayment?.amount });
    // Calculate amount from channels if amount is 0
    let amountToSet = selectedPayment?.amount || 0;
    if (amountToSet === 0 && selectedPayment?.channels && selectedPayment.channels.length > 0) {
      // Try to get amount from channels if available in the payment object
      // This is a fallback - the amount should already be calculated in mapApiPaymentToPayment
      console.log('Amount is 0, checking channels', selectedPayment);
    }
    setRefundAmount(amountToSet > 0 ? amountToSet : '');
    setRefundReason('');
    setRefundNotes('');
    setRefundError(null);
    setRefundDialogOpen(true);
    handleMenuClose();
  };

  const handleProcessRefund = async () => {
    if (!selectedPayment) {
      setRefundError('Please select a payment to refund.');
      return;
    }

    // Validate refund amount
    const amountToRefund = typeof refundAmount === 'number' ? refundAmount : (refundAmount !== '' ? Number(refundAmount) : 0);
    if (!amountToRefund || amountToRefund <= 0) {
      setRefundError('Please enter a valid refund amount greater than 0.');
      return;
    }
    // Only validate against payment amount if payment amount is greater than 0
    const paymentAmount = selectedPayment.amount || 0;
    if (paymentAmount > 0 && amountToRefund > paymentAmount) {
      setRefundError(`Refund amount cannot exceed the original payment amount of $${paymentAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`);
      return;
    }

    // Validate refund reason
    if (!refundReason || refundReason.trim() === '') {
      setRefundError('Please select a refund reason.');
      return;
    }

    try {
      setIsProcessingRefund(true);
      setRefundError(null);
      const response = await refundPayment(selectedPayment.id, {
        amount: amountToRefund,
        reason: refundReason,
        notes: refundNotes || undefined
      });
      await fetchPayments();
      setRefundDialogOpen(false);
      // Reset form
      setRefundAmount('');
      setRefundReason('');
      setRefundNotes('');
      // Show success message with refund period info
      setError(null);
      setSuccessMessage(response.message || 'Refund request submitted successfully. Your refund will be processed within 5-10 business days after admin approval.');
    } catch (err: any) {
      console.error('Refund request failed:', err);
      setRefundError(err.response?.data?.message || err.message || 'Failed to submit refund request. Please try again.');
    } finally {
      setIsProcessingRefund(false);
    }
  };

  const handleExportPayments = () => {
    try {
      // Create CSV content
      const headers = ['Transaction ID', 'Amount', 'Currency', 'Status', 'Payment Method', 'Description', 'Date', 'Channels'];
      const rows = filteredPayments.map(payment => [
        payment.transactionId || payment.id,
        payment.amount.toFixed(2),
        payment.currency,
        payment.status,
        payment.paymentMethod,
        payment.description || payment.prTitle || 'N/A',
        new Date(payment.createdAt).toLocaleDateString(),
        (payment.totalChannels || payment.channels.length).toString()
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `payments-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccessMessage('Payments exported successfully!');
    } catch (err: any) {
      console.error('Export failed:', err);
      setError('Failed to export payments. Please try again.');
    }
  };

  const handleOpenRefundDialog = () => {
    // Find first completed payment that can be refunded
    const refundablePayment = filteredPayments.find(
      p => p.status === 'completed' && !p.refundRequestStatus && !p.refundAmount
    );

    if (refundablePayment) {
      setSelectedPayment(refundablePayment);
      // Set refund amount as number (not empty string)
      setRefundAmount(typeof refundablePayment.amount === 'number' ? refundablePayment.amount : Number(refundablePayment.amount) || '');
      setRefundReason('');
      setRefundNotes('');
      setRefundError(null);
      setRefundDialogOpen(true);
    } else {
      setError('No refundable payments found. Please select a completed payment from the table that has not been refunded.');
      setTimeout(() => setError(null), 5000);
    }
  };

  const buildBrandedDocumentHtml = (payment: Payment, type: 'receipt' | 'invoice' = 'receipt') => {
    const date = new Date(payment.createdAt).toLocaleString();
    const title = type === 'invoice' ? 'Invoice' : 'Payment Receipt';
    const headline = type === 'invoice' ? 'Invoice' : 'Payment Receipt';
    const publicationsCount = (payment.totalChannels || payment.channels.length);

    return `
      <html>
        <head>
          <title>${title} - ${payment.transactionId}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; margin: 24px; color: #12212f; background-color: #f5f7fb; }
            .brand-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 16px 20px;
              border-radius: 12px;
              background: linear-gradient(135deg, #09273b, #0c3f5f);
              color: #ffffff;
              margin-bottom: 24px;
            }
            .brand-title {
              font-size: 22px;
              font-weight: 700;
              letter-spacing: 0.04em;
              text-transform: uppercase;
            }
            .brand-subtitle {
              font-size: 12px;
              opacity: 0.85;
            }
            .badge {
              padding: 6px 12px;
              border-radius: 999px;
              font-size: 11px;
              font-weight: 600;
              background-color: rgba(255, 255, 255, 0.14);
              border: 1px solid rgba(255, 255, 255, 0.3);
            }
            .container {
              background-color: #ffffff;
              border-radius: 12px;
              padding: 20px;
              box-shadow: 0 8px 24px rgba(15, 35, 52, 0.12);
            }
            h1 {
              font-size: 20px;
              margin: 0 0 4px 0;
              color: #09273b;
            }
            h2 {
              font-size: 14px;
              margin: 18px 0 8px 0;
              color: #12212f;
            }
            .meta-row {
              display: flex;
              justify-content: space-between;
              flex-wrap: wrap;
              font-size: 12px;
              color: #4b5b6a;
              margin-bottom: 16px;
            }
            .meta-col {
              margin-bottom: 4px;
            }
            .meta-label {
              font-weight: 600;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 6px;
              font-size: 13px;
            }
            th, td {
              text-align: left;
              padding: 8px 6px;
              border-bottom: 1px solid #e2e6f0;
            }
            th {
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.06em;
              color: #7a8794;
            }
            .total-row td {
              font-weight: 700;
              border-top: 2px solid #d0d7e5;
              border-bottom: none;
            }
            .status-pill {
              display: inline-flex;
              align-items: center;
              padding: 3px 10px;
              border-radius: 999px;
              font-size: 11px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.08em;
              background-color: #e3f4ea;
              color: #237a3b;
            }
            .status-pill.failed, .status-pill.cancelled {
              background-color: #fde4e4;
              color: #c0392b;
            }
            .status-pill.pending {
              background-color: #fff4dd;
              color: #c27d0e;
            }
            .footer-note {
              margin-top: 18px;
              font-size: 11px;
              color: #7a8794;
            }
            .channels {
              margin-top: 4px;
              font-size: 11px;
              color: #7a8794;
            }
            @media print {
              body { background-color: #ffffff; margin: 0; }
              .container { box-shadow: none; border-radius: 0; }
            }
          </style>
        </head>
        <body>
          <div class="brand-header">
            <div>
              <div class="brand-title">CORPCRUNCH</div>
              <div class="brand-subtitle">Press Release & Distribution Platform</div>
            </div>
            <div class="badge">
              ${headline}
            </div>
          </div>

          <div class="container">
            <h1>${headline}</h1>
            <p style="font-size: 12px; margin: 0 0 12px 0; color: #4b5b6a;">
              Thank you for using CorpCrunch for your press release distribution.
            </p>

            <div class="meta-row">
              <div class="meta-col">
                <div class="meta-label">Transaction ID</div>
                <div>${payment.transactionId}</div>
              </div>
              <div class="meta-col">
                <div class="meta-label">Date</div>
                <div>${date}</div>
              </div>
              <div class="meta-col">
                <div class="meta-label">Status</div>
                <div class="status-pill ${payment.status}">${payment.status.toUpperCase()}</div>
              </div>
              <div class="meta-col">
                <div class="meta-label">Mode</div>
                <div>${payment.stripeLiveMode ? 'Live' : 'Test'}</div>
              </div>
            </div>

            <h2>Payment Details</h2>
            <table>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Amount</th>
              </tr>
              <tr>
                <td>
                  ${payment.prTitle || 'Press Release Distribution'}
                  <div class="channels">
                    ${publicationsCount} publication${publicationsCount !== 1 ? 's' : ''} selected
                  </div>
                </td>
                <td>1</td>
                <td>$${(payment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td>$${(payment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              </tr>
              <tr class="total-row">
                <td colspan="3">Total (${payment.currency})</td>
                <td>$${(payment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              </tr>
            </table>

            <h2>Stripe & Technical Details</h2>
            <table>
              <tr>
                <th>Field</th>
                <th>Value</th>
              </tr>
              <tr>
                <td>Stripe Payment Intent</td>
                <td>${payment.stripePaymentIntentId || 'N/A'}</td>
              </tr>
              <tr>
                <td>Stripe Session ID</td>
                <td>${payment.stripeSessionId || 'N/A'}</td>
              </tr>
              <tr>
                <td>Publications</td>
                <td>${publicationsCount} publication${publicationsCount !== 1 ? 's' : ''}</td>
              </tr>
            </table>

            <p class="footer-note">
              This ${type === 'invoice' ? 'invoice' : 'receipt'} was generated by CorpCrunch. 
              For any billing questions, please contact support@corpcrunch.io.
            </p>
          </div>
        </body>
      </html>
    `;
  };

  const handleDownloadReceipt = () => {
    if (!selectedPayment) return;
    const html = buildBrandedDocumentHtml(selectedPayment, 'receipt');
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      // Users can use the browser's "Save as PDF" option in the print dialog
      win.focus();
      win.print();
    }
    handleMenuClose();
  };

  const handlePrintInvoice = () => {
    if (!selectedPayment) return;
    const html = buildBrandedDocumentHtml(selectedPayment, 'invoice');
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      win.print();
    }
    handleMenuClose();
  };

  const handleSendReceipt = () => {
    if (!selectedPayment) return;
    // Frontend-only helper: open email client pre-filled with basic receipt info
    const subject = encodeURIComponent(`CorpCrunch Payment Receipt - ${selectedPayment.transactionId}`);
    const body = encodeURIComponent(
      `Hi,\n\n` +
      `Thank you for using CorpCrunch for your press release distribution. Here are your payment details:\n\n` +
      `Transaction ID: ${selectedPayment.transactionId}\n` +
      `Amount: $${(selectedPayment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${selectedPayment.currency}\n` +
      `Status: ${selectedPayment.status}\n` +
      `Payment Method: ${selectedPayment.paymentMethod}\n` +
      `PR Title: ${selectedPayment.prTitle}\n` +
      `Publications: ${(selectedPayment.totalChannels || selectedPayment.channels.length)}\n` +
      `Date: ${new Date(selectedPayment.createdAt).toLocaleString()}\n\n` +
      `Best regards,\nCorpCrunch Team`
    );
    const mailto = `mailto:${selectedPayment.customerEmail || ''}?subject=${subject}&body=${body}`;
    window.location.href = mailto;
    handleMenuClose();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <Typography variant="h4" style={{ fontWeight: 'bold', color: '#0927eb', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <PaymentIcon style={{ fontSize: '32px' }} />
          Payments
        </Typography>
        <Typography variant="body1" style={{ color: '#666' }}>
          Manage and track all payment transactions
        </Typography>
      </div>

      {/* Loading & Error States */}
      {loading && (
        <Box mb={2}>
          <LinearProgress />
        </Box>
      )}
      {error && (
        <Box mb={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      {successMessage && (
        <Box mb={2}>
          <Alert severity="success" onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        </Box>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} style={{ marginBottom: '32px' }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#0927eb' }}>
                    {payments.length}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Total Payments
                  </Typography>
                </div>
                <PaymentIcon style={{ fontSize: '40px', color: '#0927eb', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#4CAF50' }}>
                    ${payments.filter(p => p.status === 'completed').reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Total Amount
                  </Typography>
                </div>
                <TrendingUpIcon style={{ fontSize: '40px', color: '#4CAF50', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#FF9800' }}>
                    {payments.filter(p => p.status === 'pending').length}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Pending
                  </Typography>
                </div>
                <PendingIcon style={{ fontSize: '40px', color: '#FF9800', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#2196F3' }}>
                    ${payments.filter(p => p.status === 'refunded').reduce((sum, p) => sum + (p.refundAmount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Total Refunds
                  </Typography>
                </div>
                <TrendingDownIcon style={{ fontSize: '40px', color: '#2196F3', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper style={{ padding: '24px', marginBottom: '24px', borderRadius: '12px' }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search payments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="refunded">Refunded</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Method</InputLabel>
              <Select
                value={methodFilter}
                onChange={(e) => setMethodFilter(e.target.value)}
                label="Method"
              >
                <MenuItem value="all">All Methods</MenuItem>
                <MenuItem value="credit_card">Credit Card</MenuItem>
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                <MenuItem value="stripe">Stripe</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<ReceiptIcon />}
              fullWidth
              onClick={handleExportPayments}
              style={{
                borderColor: '#000000',
                color: '#000000'
              }}
            >
              Export
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              onClick={handleOpenRefundDialog}
              style={{
                backgroundColor: '#000000',
                color: 'white',
                fontWeight: 'bold'
              }}
            >
              Process Refund
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Payments Table */}
      <TableContainer component={Paper} style={{ borderRadius: '12px' }}>
        <Table>
          <TableHead>
            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
              <TableCell style={{ fontWeight: 'bold' }}>Transaction</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Amount</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Method</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Description</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Date</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPayments
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell>
                    <div>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        {payment.transactionId}
                      </Typography>
                      <Typography variant="body2" style={{ color: '#666' }}>
                        {payment.prTitle}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: getDisplayStatus(payment) === 'refunded' ? '#F44336' : '#4CAF50' }}>
                      ${(payment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                    {payment.refundAmount && payment.refundRequestStatus === 'processed' && (
                      <Typography variant="body2" style={{ color: '#F44336' }}>
                        Refund: ${payment.refundAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    )}
                    {payment.refundRequestStatus === 'pending' && (
                      <Typography variant="body2" style={{ color: '#FF9800' }}>
                        Refund requested: ${(payment.refundAmount || payment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    )}
                    {payment.refundRequestStatus === 'approved' && (
                      <Typography variant="body2" style={{ color: '#2196F3' }}>
                        Refund approved: ${(payment.refundAmount || payment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {getPaymentMethodIcon(payment.paymentMethod)}
                      <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                        {payment.paymentMethod.replace('_', ' ')}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(getDisplayStatus(payment), payment.refundRequestStatus)}
                      label={getDisplayStatus(payment).charAt(0).toUpperCase() + getDisplayStatus(payment).slice(1)}
                      style={{
                        backgroundColor: getStatusColor(getDisplayStatus(payment), payment.refundRequestStatus),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                    {payment.refundRequestStatus === 'pending' && (
                      <Typography variant="caption" style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '0.7rem' }}>
                        Refund requested
                      </Typography>
                    )}
                    {payment.refundRequestStatus === 'approved' && (
                      <Typography variant="caption" style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '0.7rem' }}>
                        Refund approved
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payment.description}
                    </Typography>
                    {(payment.totalChannels || payment.channels.length) > 0 && (
                      <Typography variant="caption" style={{ color: '#999', display: 'block', marginTop: '4px' }}>
                        {(payment.totalChannels || payment.channels.length)} publication
                        {(payment.totalChannels || payment.channels.length) !== 1 ? 's' : ''}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <CalendarIcon style={{ fontSize: '16px', color: '#666' }} />
                      {new Date(payment.createdAt).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) => handleMenuClick(e, payment)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredPayments.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewPayment}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownloadReceipt}>
          <ListItemIcon>
            <ReceiptIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download Receipt</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePrintInvoice}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print Invoice</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleSendReceipt}>
          <ListItemIcon>
            <EmailIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Send Receipt</ListItemText>
        </MenuItem>
      </Menu>

      {/* View Payment Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" style={{ fontWeight: 'bold' }}>
            Payment Details - {selectedPayment?.transactionId}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <div style={{ marginTop: '16px' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Transaction Information
                  </Typography>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                      Transaction ID
                    </Typography>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {selectedPayment.transactionId}
                    </Typography>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                      Amount
                    </Typography>
                    <Typography variant="h6" style={{ fontWeight: 'bold', color: '#4CAF50' }}>
                      ${(selectedPayment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {selectedPayment.currency}
                    </Typography>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                      Payment Method
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {getPaymentMethodIcon(selectedPayment.paymentMethod)}
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                        {selectedPayment.paymentMethod.replace('_', ' ')}
                      </Typography>
                    </div>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                      Publications
                    </Typography>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {(selectedPayment.totalChannels || selectedPayment.channels.length)} publication
                      {(selectedPayment.totalChannels || selectedPayment.channels.length) !== 1 ? 's' : ''}
                    </Typography>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                      Status
                    </Typography>
                    <Chip
                      icon={getStatusIcon(getDisplayStatus(selectedPayment), selectedPayment.refundRequestStatus)}
                      label={getDisplayStatus(selectedPayment).charAt(0).toUpperCase() + getDisplayStatus(selectedPayment).slice(1)}
                      style={{
                        backgroundColor: getStatusColor(getDisplayStatus(selectedPayment), selectedPayment.refundRequestStatus),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                    {selectedPayment.refundRequestStatus === 'pending' && (
                      <Typography variant="caption" style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                        Refund request is pending admin review
                      </Typography>
                    )}
                    {selectedPayment.refundRequestStatus === 'approved' && (
                      <Typography variant="caption" style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                        Refund request has been approved and is being processed
                      </Typography>
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Customer Information
                  </Typography>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                      Customer Name
                    </Typography>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {selectedPayment.customerName || 'N/A'}
                    </Typography>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                      Email
                    </Typography>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {selectedPayment.customerEmail || 'N/A'}
                    </Typography>
                  </div>
                  {selectedPayment.billingAddress && (
                    <div style={{ marginBottom: '16px' }}>
                      <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                        Billing Address
                      </Typography>
                      <Typography variant="body2">
                        {selectedPayment.billingAddress.street}<br />
                        {selectedPayment.billingAddress.city}, {selectedPayment.billingAddress.state} {selectedPayment.billingAddress.zipCode}<br />
                        {selectedPayment.billingAddress.country}
                      </Typography>
                    </div>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Payment Details
                  </Typography>
                  <Typography variant="body2" style={{ marginBottom: '16px' }}>
                    {selectedPayment.description}
                  </Typography>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                      Channels
                    </Typography>
                    <div>
                      {selectedPayment.channels.map((channel, index) => (
                        <Chip
                          key={index}
                          label={channel}
                          size="small"
                          style={{ marginRight: '8px', marginBottom: '4px' }}
                        />
                      ))}
                    </div>
                  </div>
                  {selectedPayment.metadata?.taxAmount && (
                    <div style={{ marginBottom: '16px' }}>
                      <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                        Tax Amount
                      </Typography>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        ${selectedPayment.metadata?.taxAmount}
                      </Typography>
                    </div>
                  )}
                  {selectedPayment.refundRequestStatus && (
                    <Alert 
                      severity={selectedPayment.refundRequestStatus === 'pending' ? 'warning' : selectedPayment.refundRequestStatus === 'approved' ? 'info' : selectedPayment.refundRequestStatus === 'processed' ? 'success' : 'error'} 
                      style={{ marginBottom: '16px' }}
                    >
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        Refund Request Status: {selectedPayment.refundRequestStatus.charAt(0).toUpperCase() + selectedPayment.refundRequestStatus.slice(1)}
                      </Typography>
                      <Typography variant="body2">
                        {selectedPayment.refundRequestStatus === 'pending' && 'Your refund request is pending admin review.'}
                        {selectedPayment.refundRequestStatus === 'approved' && 'Your refund request has been approved and is being processed.'}
                        {selectedPayment.refundRequestStatus === 'processed' && 'Your refund has been processed successfully.'}
                        {selectedPayment.refundRequestStatus === 'rejected' && 'Your refund request has been rejected. Please contact support for more information.'}
                        <br />
                        Requested Amount: ${(selectedPayment.refundAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}<br />
                        Refund Reason: {selectedPayment.refundReason || 'N/A'}<br />
                        Requested Date: {selectedPayment.refundRequestedAt ? new Date(selectedPayment.refundRequestedAt).toLocaleString() : 'N/A'}
                        {selectedPayment.refundedAt && <><br />Processed Date: {new Date(selectedPayment.refundedAt).toLocaleString()}</>}
                      </Typography>
                    </Alert>
                  )}
                  {selectedPayment.refundAmount && !selectedPayment.refundRequestStatus && (
                    <Alert severity="success" style={{ marginBottom: '16px' }}>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        Refund Processed
                      </Typography>
                      <Typography variant="body2">
                        Refund Amount: ${(selectedPayment.refundAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}<br />
                        Refund Reason: {selectedPayment.refundReason || 'N/A'}<br />
                        Refund Date: {selectedPayment.refundedAt ? new Date(selectedPayment.refundedAt).toLocaleString() : 'N/A'}
                      </Typography>
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<ReceiptIcon />}
            style={{
              backgroundColor: '#000000',
              color: 'white'
            }}
            onClick={handleDownloadReceipt}
          >
            Download Receipt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog
        open={refundDialogOpen}
        onClose={() => setRefundDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" style={{ fontWeight: 'bold' }}>
            Request Refund
          </Typography>
        </DialogTitle>
        <DialogContent>
          {refundError && (
            <Box mb={2}>
              <Alert severity="error">{refundError}</Alert>
            </Box>
          )}
          <Box mb={2}>
            <Alert severity="info">
              <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                Refund Processing Information
              </Typography>
              <Typography variant="body2">
                Your refund request will be reviewed by our admin team. Once approved, the refund will be processed through your original payment method (Stripe or other). 
                <strong> The money will be returned to your account within 5-10 business days</strong> after approval.
              </Typography>
            </Alert>
          </Box>
          <Grid container spacing={3} style={{ marginTop: '16px' }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                Transaction: {selectedPayment?.transactionId}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '16px' }}>
                Original Amount: ${selectedPayment ? (selectedPayment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
              </Typography>
              {selectedPayment && (!selectedPayment.amount || selectedPayment.amount === 0) && (
                <Typography variant="caption" style={{ display: 'block', color: '#FF9800', marginTop: '4px' }}>
                  Note: If amount shows $0.00, please enter the refund amount manually based on your payment receipt.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Refund Amount"
                type="number"
                placeholder="Enter refund amount"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value === '' ? '' : Number(e.target.value))}
                helperText={
                  refundAmount !== '' && selectedPayment && (Number(refundAmount) > selectedPayment.amount || Number(refundAmount) <= 0)
                    ? `Amount must be greater than 0 and not exceed $${(selectedPayment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : ''
                }
                error={
                  refundAmount !== '' &&
                  selectedPayment !== null &&
                  (Number(refundAmount) > (selectedPayment?.amount || 0) || Number(refundAmount) <= 0)
                }
                inputProps={{
                  style: { 
                    MozAppearance: 'textfield'
                  }
                }}
                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield'
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Refund Reason</InputLabel>
                <Select
                  label="Refund Reason"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value as string)}
                >
                 
                  <MenuItem value="duplicate_payment">Duplicate Payment</MenuItem>
                  <MenuItem value="billing_error">Billing Error</MenuItem>
                 
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                multiline
                rows={3}
                placeholder="Add any additional notes about the refund"
                value={refundNotes}
                onChange={(e) => setRefundNotes(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            style={{
              backgroundColor: '#F44336',
              color: 'white'
            }}
            disabled={(() => {
              if (isProcessingRefund || !selectedPayment) {
                return true;
              }
              
              // Check refund amount
              const amount = typeof refundAmount === 'number' ? refundAmount : (refundAmount !== '' ? Number(refundAmount) : 0);
              const paymentAmount = selectedPayment.amount || 0;
              
              if (!amount || amount <= 0 || isNaN(amount)) {
                return true;
              }
              
              // Only validate against payment amount if payment amount is greater than 0
              if (paymentAmount > 0 && amount > paymentAmount) {
                return true;
              }
              
              // Check refund reason
              if (!refundReason || refundReason.trim() === '') {
                return true;
              }
              
              return false;
            })()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Submit Refund Request clicked', {
                selectedPayment: !!selectedPayment,
                refundAmount,
                refundReason,
                isProcessingRefund,
                disabled: isProcessingRefund || !selectedPayment
              });
              if (!isProcessingRefund && selectedPayment) {
                handleProcessRefund();
              } else {
                console.warn('Button click ignored - processing:', isProcessingRefund, 'or no payment:', !selectedPayment);
              }
            }}
          >
            {isProcessingRefund ? 'Submitting...' : 'Submit Refund Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Payments;
